/**
 * TeamCard — unified "Team" view for a project.
 *
 * Replaces the two side-by-side cards (Team Assignments + Team
 * Capacity) with a single card that does both jobs in one mental
 * model. The roster IS the capacity table — every assigned person
 * appears once, with their commitment on this work alongside their
 * total load and the verdict everyone reads first.
 *
 * Affordances preserved from the old cards:
 *   - Assign person  (inline form, role + hours/wk)
 *   - Edit hours/wk inline (click the number)
 *   - Delete an assignment (trash icon)
 *   - Click a name → navigates to /people/:name (the person detail page)
 *
 * Added by this merge:
 *   - One header, one icon, one set of headings
 *   - Verdict pill + summary at the top
 *   - 12-week utilization strip per person
 *
 * Math runs client-side (see comment block in the previous
 * TeamCapacityPanel) so the panel works regardless of which backend
 * commit is live.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, UserPlus, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import { avatarTone } from "@/lib/format";
import {
  useProjectAssignments,
  useCreateAssignment,
  useDeleteAssignment,
  useAllAssignments,
} from "@/hooks/useAssignments";
import { useRoster } from "@/hooks/useRoster";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useCapacityTiers } from "@/hooks/useCapacityRebuild";
import { countsForCapacity } from "@/lib/capacityScope";
import { ScheduleHintLine } from "@/components/capacity/ScheduleHintLine";

const ROLE_LABEL: Record<string, string> = {
  pm: "PM",
  ba: "BA",
  functional: "Functional",
  technical: "Technical",
  developer: "Developer",
  infrastructure: "Infrastructure",
  dba: "DBA",
  erp: "ERP",
  operations: "Operations",
  "wms consultant": "WMS Consultant",
};

type Verdict = "ok" | "tight" | "at_risk" | "empty";

interface PersonRowData {
  /** Stable key for React. */
  key: string;
  /** Display name (roster casing if available, otherwise input casing). */
  name: string;
  role_key: string | null;
  /** Hours per week this person is committed to on this project. */
  hours_here: number;
  /** Their TOTAL weekly demand across everything they're on. */
  total_hours_week: number;
  capacity_hrs_week: number;
  utilization_pct: number;
  /** Roster lookup miss — surface in amber so the gap is visible. */
  not_on_roster: boolean;
}

interface Props {
  entityKind: "project";
  entityId: string;
  /** When set, render the "earliest start / bottleneck" hint line
   *  inside the Team card. The Team card itself doesn't know whether
   *  a project is pre-start vs. in-flight vs. terminal — the caller
   *  decides whether the question is relevant and passes the projectId
   *  in. */
  scheduleHintProjectId?: string | null;
}

// ---------------------------------------------------------------------------
// Visual helpers — same palette the existing heatmap uses.
// ---------------------------------------------------------------------------

function utilTextClass(util: number): string {
  if (util >= 1.0) return "text-red-700 dark:text-red-300";
  if (util >= 0.8) return "text-amber-700 dark:text-amber-300";
  if (util > 0) return "text-emerald-700 dark:text-emerald-300";
  return "text-ink-subtle";
}

function verdictStyle(v: Verdict) {
  switch (v) {
    case "at_risk":
      return { pill: "bg-red-100 text-red-800 dark:text-red-300 ring-red-300", banner: "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30", label: "At Risk" };
    case "tight":
      return { pill: "bg-amber-100 text-amber-800 dark:text-amber-300 ring-amber-300", banner: "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30", label: "Tight" };
    case "ok":
      return { pill: "bg-emerald-100 text-emerald-800 dark:text-emerald-300 ring-emerald-300", banner: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30", label: "OK" };
    case "empty":
    default:
      return { pill: "bg-raised text-ink-3 ring-line", banner: "bg-canvas border-line", label: "No team" };
  }
}

// ---------------------------------------------------------------------------

export function TeamCard({ entityKind, entityId, scheduleHintProjectId }: Props) {
  // ---- Data ----------------------------------------------------------
  // Flat capacity model (2026-09-14): TOTAL = every explicit hour on
  // every project assignment; CAP = roster availability after support
  // reserve. Identical math to /capacity's people ledger, so the two
  // pages can never disagree (Brett's Emily Fridley report: this card
  // used the old dated engine and showed 15/35 while /capacity said
  // 30/26.3 — same rows, two calculators).
  const allAssignmentsQ = useAllAssignments();
  // Capacity scope (2026-10-06): a person's total only counts hours on
  // projects in the bands chosen on Admin — same rule as /capacity.
  const portfolioQ = usePortfolio(false);
  const capacityTiers = useCapacityTiers();
  const rosterQ = useRoster();

  const directProjectAssignmentsQ = useProjectAssignments(
    entityKind === "project" ? entityId : null,
  );

  // ---- Mutations -----------------------------------------------------
  const createProjectAssignment = useCreateAssignment();
  const deleteProjectAssignment = useDeleteAssignment();

  // ---- UI state ------------------------------------------------------
  const [adding, setAdding] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [newHrs, setNewHrs] = useState("0");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editHrs, setEditHrs] = useState("");
  const navigate = useNavigate();

  // ---- Loading / error gates ----------------------------------------
  const isLoading =
    allAssignmentsQ.isLoading ||
    rosterQ.isLoading ||
    (entityKind === "project" && directProjectAssignmentsQ.isLoading);

  // ---- Derived rows --------------------------------------------------
  const derived = useMemo(() => {
    if (!Array.isArray(allAssignmentsQ.data) || !Array.isArray(rosterQ.data)) return null;

    // One row per assignment record so the edit affordances remain
    // per-record. The verdict math sums across everything the person
    // is on (allAssignmentsQ reflects total demand).
    type RowSeed = {
      person_key: string;
      person_name_raw: string;
      role_key: string;
      hours: number;
    };
    const seeds: RowSeed[] = [];

    for (const a of directProjectAssignmentsQ.data ?? []) {
      if (!a.person_name) continue;
      seeds.push({
        person_key: a.person_name.trim().toLowerCase(),
        person_name_raw: a.person_name.trim(),
        role_key: a.role_key,
        hours: a.hours_per_week != null ? Number(a.hours_per_week) : 0,
      });
    }

    // Flat totals — same math as /capacity's people ledger.
    const rosterByKey = new Map(
      rosterQ.data.map((m) => [m.name.trim().toLowerCase(), m]),
    );
    const projectById = new Map((Array.isArray(portfolioQ.data) ? portfolioQ.data : []).map((p) => [p.id, p]));
    const totalByKey = new Map<string, number>();
    for (const a of allAssignmentsQ.data) {
      if (!countsForCapacity(projectById.get(a.project_id), capacityTiers)) continue;
      const k = a.person_name.trim().toLowerCase();
      totalByKey.set(k, (totalByKey.get(k) ?? 0) + (a.hours_per_week ?? 0));
    }

    const rows: PersonRowData[] = seeds.map((s) => {
      const member = rosterByKey.get(s.person_key);
      if (!member) {
        return {
          key: `direct-${s.person_key}-${s.role_key}`,
          name: s.person_name_raw,
          role_key: s.role_key,
          hours_here: Math.round(s.hours * 10) / 10,
          total_hours_week: 0,
          capacity_hrs_week: 0,
          utilization_pct: 0,
          not_on_roster: true,
        };
      }
      const total = totalByKey.get(s.person_key) ?? 0;
      const cap = member.project_capacity_hrs;
      return {
        key: `direct-${s.person_key}-${s.role_key}`,
        name: member.name,
        role_key: s.role_key,
        hours_here: Math.round(s.hours * 10) / 10,
        total_hours_week: Math.round(total * 10) / 10,
        capacity_hrs_week: Math.round(cap * 10) / 10,
        utilization_pct: cap > 0 ? total / cap : 0,
        not_on_roster: false,
      };
    });

    // Verdict math: per unique person, take the worst utilization. Don't
    // count "not on roster" rows since we don't know their load.
    const utilByPerson = new Map<string, number>();
    for (const r of rows) {
      if (r.not_on_roster) continue;
      const prev = utilByPerson.get(r.name.toLowerCase()) ?? 0;
      if (r.utilization_pct > prev) utilByPerson.set(r.name.toLowerCase(), r.utilization_pct);
    }
    const utils = Array.from(utilByPerson.values());
    const atRisk = utils.filter((u) => u >= 1.0).length;
    const tight = utils.filter((u) => u >= 0.8 && u < 1.0).length;
    const total = utils.length;

    // Sort: at-risk → tight → others. Then by util desc.
    rows.sort((a, b) => {
      if (a.not_on_roster !== b.not_on_roster) return a.not_on_roster ? 1 : -1;
      if (a.utilization_pct !== b.utilization_pct) return b.utilization_pct - a.utilization_pct;
      return a.name.localeCompare(b.name);
    });

    let verdict: Verdict;
    let summary: string;
    if (total === 0 && rows.length === 0) {
      verdict = "empty";
      summary = "No team assigned yet.";
    } else if (total === 0) {
      verdict = "empty";
      summary = "Assigned team members aren't on the roster — capacity can't be checked yet.";
    } else if (atRisk > 0) {
      verdict = "at_risk";
      summary = `${atRisk} of ${total} overcommitted — this is at risk.`;
    } else if (tight > 0) {
      verdict = "tight";
      summary = `${tight} of ${total} at ≥80% — tight but workable.`;
    } else {
      verdict = "ok";
      summary = `All ${total} assigned have headroom.`;
    }

    return { rows, verdict, summary };
  }, [
    allAssignmentsQ.data,
    portfolioQ.data,
    capacityTiers,
    rosterQ.data,
    directProjectAssignmentsQ.data,
  ]);

  // ---- Assign form helpers ------------------------------------------
  const roster = Array.isArray(rosterQ.data) ? rosterQ.data : [];
  const directAssignedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const a of directProjectAssignmentsQ.data ?? []) {
      s.add(`${a.person_name}::${a.role_key}`);
    }
    return s;
  }, [directProjectAssignmentsQ.data]);

  const handlePersonSelect = (name: string) => {
    setSelectedPerson(name);
    const member = roster.find((m) => m.name === name);
    if (member) setSelectedRole(member.role_key);
  };

  const handleAdd = () => {
    if (!selectedPerson || !selectedRole) return;
    const hrs = Math.max(0, parseFloat(newHrs) || 0);
    const reset = () => {
      setAdding(false);
      setSelectedPerson("");
      setSelectedRole("");
      setNewHrs("0");
    };
    createProjectAssignment.mutate(
      { projectId: entityId, person_name: selectedPerson, role_key: selectedRole, allocation_pct: 1.0, hours_per_week: hrs },
      { onSuccess: reset },
    );
  };

  const handleEditHoursSave = (row: PersonRowData) => {
    const hrs = Math.max(0, parseFloat(editHrs) || 0);
    createProjectAssignment.mutate(
      { projectId: entityId, person_name: row.name, role_key: row.role_key ?? "", allocation_pct: 1.0, hours_per_week: hrs },
      { onSuccess: () => setEditingKey(null) },
    );
  };

  const handleRemoveDirect = (row: PersonRowData) => {
    deleteProjectAssignment.mutate({ projectId: entityId, person_name: row.name, role_key: row.role_key ?? "" });
  };

  // ---- Render --------------------------------------------------------
  if (isLoading || !derived) {
    return (
      <div className="rounded-xl border border-line bg-surface p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Users className="h-4 w-4 text-brand-navy-500" />
          Team
        </div>
        <div className="mt-2 text-xs text-ink-subtle">Loading…</div>
      </div>
    );
  }

  const style = verdictStyle(derived.verdict);

  return (
    <div className="rounded-xl border border-line bg-surface p-5">
      {/* Header — one title, the verdict, and the assign-person CTA. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-navy-500" />
          <div>
            <div className="text-sm font-semibold text-ink">Team</div>
            <p className="mt-0.5 text-xs text-ink-muted">
              Who's on this work — and whether they have the capacity to deliver it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1",
              style.pill,
            )}
          >
            {style.label}
          </span>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 rounded-md border border-brand-navy-200 dark:border-brand-navy-500/30 bg-surface px-2 py-1 text-[11px] font-medium text-brand-navy-700 dark:text-brand-navy-300 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 transition-colors"
            >
              <UserPlus className="h-3 w-3" />
              Assign person
            </button>
          )}
        </div>
      </div>

      {/* Verdict banner */}
      <div className={cn("mt-3 rounded-md border px-3 py-2 text-xs text-ink-2", style.banner)}>
        {derived.summary}
      </div>

      {/* Schedule hint — pre-start projects only. Answers the natural
          follow-up to "do they have capacity?": "and when does that
          mean the project can actually start?" */}
      {scheduleHintProjectId && (
        <div className="mt-2">
          <ScheduleHintLine projectId={scheduleHintProjectId} />
        </div>
      )}

      {/* Inline assign form */}
      {adding && (
        <div className="mt-3 rounded-md border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/30 dark:bg-brand-navy-500/10 p-3 space-y-2">
          <select
            value={selectedPerson}
            onChange={(e) => handlePersonSelect(e.target.value)}
            className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
          >
            <option value="">Select person…</option>
            {roster
              .filter((m) => !directAssignedKeys.has(`${m.name}::${m.role_key}`))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((m) => (
                <option key={m.name} value={m.name}>
                  {m.name} ({ROLE_LABEL[m.role_key] ?? m.role_key})
                </option>
              ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
            >
              <option value="">Select role…</option>
              {Object.entries(ROLE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <div className="relative">
              <input
                type="number"
                min={0}
                step={1}
                value={newHrs}
                onChange={(e) => setNewHrs(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-center tabular-nums focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
                placeholder="Hours/week"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-ink-subtle">h/wk</span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setAdding(false)}
              className="rounded px-2 py-1 text-xs font-medium text-ink-muted hover:bg-raised transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={
                !selectedPerson ||
                !selectedRole ||
                createProjectAssignment.isPending
              }
              className="flex items-center gap-1 rounded bg-brand-navy-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
            >
              <Plus className="h-3 w-3" />
              {createProjectAssignment.isPending ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {/* Roster table */}
      {derived.rows.length > 0 ? (
        <div className="mt-4 space-y-1">
          <div className="grid grid-cols-12 gap-3 px-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            <span className="col-span-4">Person</span>
            <span className="col-span-2">Role</span>
            <span className="col-span-2">On this</span>
            <span className="col-span-2">Total / cap</span>
            <span className="col-span-2">Util</span>
          </div>
          {derived.rows.map((r) => (
            <TeamRow
              key={r.key}
              row={r}
              isEditing={editingKey === r.key}
              editHrs={editHrs}
              onClickName={() => navigate(`/people/${encodeURIComponent(r.name)}`)}
              onClickHours={() => {
                setEditingKey(r.key);
                setEditHrs(r.hours_here.toString());
              }}
              onChangeEditHrs={setEditHrs}
              onBlurEditHrs={() => handleEditHoursSave(r)}
              onCancelEdit={() => setEditingKey(null)}
              onRemove={() => handleRemoveDirect(r)}
            />
          ))}
        </div>
      ) : !adding ? (
        <div className="mt-4 rounded-md border border-dashed border-line px-3 py-4 text-center text-xs text-ink-subtle">
          No one is assigned to this yet. Click <strong>Assign person</strong> to add the team.
        </div>
      ) : null}

    </div>
  );
}

// ---------------------------------------------------------------------------
// One row in the team table. Every row is a direct assignment, editable.
// ---------------------------------------------------------------------------

function TeamRow({
  row,
  isEditing,
  editHrs,
  onClickName,
  onClickHours,
  onChangeEditHrs,
  onBlurEditHrs,
  onCancelEdit,
  onRemove,
}: {
  row: PersonRowData;
  isEditing: boolean;
  editHrs: string;
  onClickName: () => void;
  onClickHours: () => void;
  onChangeEditHrs: (v: string) => void;
  onBlurEditHrs: () => void;
  onCancelEdit: () => void;
  onRemove: () => void;
}) {
  const initials = row.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (row.not_on_roster) {
    return (
      <div className="grid grid-cols-12 items-center gap-3 rounded-md bg-amber-50 dark:bg-amber-500/10 px-2 py-2 text-xs ring-1 ring-amber-200 dark:ring-amber-500/30">
        <span className="col-span-3 truncate font-medium text-amber-900 dark:text-amber-300">{row.name}</span>
        <span className="col-span-1 text-amber-700 dark:text-amber-300">
          {row.role_key ? ROLE_LABEL[row.role_key] ?? row.role_key : "—"}
        </span>
        <span className="col-span-2 tabular-nums text-amber-800 dark:text-amber-300">{row.hours_here}h</span>
        <span className="col-span-6 text-[11px] italic text-amber-700 dark:text-amber-300">
          Not on roster — add them in Roster to see capacity.
        </span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 items-center gap-3 rounded-md px-2 py-2 text-xs hover:bg-raised">
      {/* Person — clickable to drill in */}
      <button
        type="button"
        onClick={onClickName}
        title="See everything this person is assigned to"
        className="col-span-4 flex items-center gap-2 min-w-0 -my-1 -ml-1 rounded-md px-1 py-1 text-left hover:bg-surface transition-colors"
      >
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
          style={{ backgroundColor: avatarTone(row.name) }}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{row.name}</div>
        </div>
      </button>

      {/* Role */}
      <span className="col-span-2 truncate text-ink-muted">
        {row.role_key ? ROLE_LABEL[row.role_key] ?? row.role_key : "—"}
      </span>

      {/* Hours on this — click to edit */}
      <div className="col-span-2 flex items-center gap-2">
        {isEditing ? (
          <span className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              step={1}
              value={editHrs}
              onChange={(e) => onChangeEditHrs(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") onCancelEdit();
              }}
              onBlur={onBlurEditHrs}
              autoFocus
              className="w-16 rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-1.5 py-0.5 text-center text-[11px] tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
            />
            <span className="text-[10px] text-ink-muted">h/wk</span>
          </span>
        ) : (
          <button
            onClick={onClickHours}
            className="tabular-nums text-ink-2 hover:text-brand-navy-600 dark:hover:text-brand-navy-400 hover:underline transition-colors cursor-pointer"
            title="Click to edit weekly hours"
          >
            <span className="font-semibold">{row.hours_here}</span>
            <span className="text-ink-subtle">h/wk</span>
          </button>
        )}
      </div>

      {/* Total / capacity */}
      <div className="col-span-2 tabular-nums text-ink-2">
        <span className="font-semibold">{row.total_hours_week}</span>
        <span className="text-ink-subtle"> / {row.capacity_hrs_week}h</span>
      </div>

      {/* Util % — flat: total explicit hours / reserve-adjusted cap,
          the same number /capacity shows for this person. */}
      <div className="col-span-1">
        <span className={cn("text-xs font-bold tabular-nums", utilTextClass(row.utilization_pct))}>
          {Math.round(row.utilization_pct * 100)}%
        </span>
      </div>

      {/* Remove */}
      <div className="col-span-1 flex items-center justify-end">
        <button
          onClick={onRemove}
          className="shrink-0 rounded p-0.5 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors"
          title="Remove assignment"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
